import { Storage } from '@aws-amplify/storage';

/**
 * Upload a captured image to S3
 *
 * IMPORTANT: Images are stored PERMANENTLY for event history/audit trail
 * - No lifecycle policies - captures are never automatically deleted
 * - Public read access enabled (since 2026-05-30)
 * - Can be accessed via direct URLs without signed URL generation
 *
 * UPLOAD FLOW:
 * 1. Frontend uploads to: public/captures/temp/{userId}/{timestamp}.jpg
 * 2. Backend Lambda (rekognitionFaceMatch) processes and moves to:
 *    - Success: public/captures/YYYY-MM-DD/{userId}/success/{timestamp}_personId.jpg
 *    - Failed: public/captures/YYYY-MM-DD/{userId}/failed/{timestamp}.jpg
 * 3. Files in temp/ are only there briefly during processing
 *
 * @param {string} dataUrl - Base64 data URL of the image
 * @param {string} userId - Current user's Cognito sub ID (can contain special chars like email)
 * @returns {Promise<string>} S3 key of uploaded image (with public/ prefix)
 */
export async function uploadCapture(dataUrl, userId) {
  try {
    // Convert data URL to blob
    const blob = dataURLtoBlob(dataUrl);

    // Create temporary capture filename: captures/temp/userId/timestamp.jpg
    // This is a TEMPORARY location - backend Lambda moves it to final location:
    //   - public/captures/YYYY-MM-DD/{userId}/success/{timestamp}_personId.jpg (if match found)
    //   - public/captures/YYYY-MM-DD/{userId}/failed/{timestamp}.jpg (if no match)
    const timestamp = Date.now();
    const captureKey = `captures/temp/${userId}/${timestamp}.jpg`;

    // Upload to S3 with public access
    // - level: 'public' enables direct URL access (no signed URLs needed)
    // - Public read access enabled on bucket (since 2026-05-30)
    // - Images are permanent for audit/compliance (no auto-deletion)
    await Storage.put(captureKey, blob, {
      contentType: 'image/jpeg',
      level: 'public',
      // Note: Could add metadata for better organization:
      // metadata: {
      //   uploadedAt: new Date().toISOString(),
      //   userId: userId
      // }
    });

    // Return the full S3 key including the public/ prefix
    // Amplify Storage adds 'public/' prefix when level: 'public'
    return `public/${captureKey}`;
  } catch (error) {
    console.error('[CaptureUpload] Upload failed:', error);
    throw new Error(`Failed to upload capture: ${error.message}`);
  }
}

/**
 * Convert data URL to Blob
 * @param {string} dataUrl - Base64 data URL
 * @returns {Blob} Image blob
 */
function dataURLtoBlob(dataUrl) {
  // Split the data URL
  const parts = dataUrl.split(',');
  const mimeString = parts[0].split(':')[1].split(';')[0];
  const byteString = atob(parts[1]);

  // Convert to byte array
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }

  return new Blob([ab], { type: mimeString });
}

/**
 * DEPRECATED (2026-05-30): Use getCapturedImageDisplayUrl from captureUrlService.js instead
 *
 * This function generated signed URLs that expired after 1 hour. We now use direct
 * public URLs that never expire (see captureUrlService.js).
 *
 * Migration:
 * - Old: const url = await getCaptureUrl(s3Key);
 * - New: import { getCapturedImageDisplayUrl } from '../services/s3/captureUrlService';
 *        const url = getCapturedImageDisplayUrl(s3Key); // Synchronous, no await needed
 *
 * This function is kept for reference only and should not be used in new code.
 */
